import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, ChevronLeft, ChevronRight, ChevronDown,
  Moon, Sun, Eye, EyeOff,
  SlidersHorizontal,
  Printer,
  CalendarDays, Menu, LayoutGrid, LayoutList,
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { usePrivacy } from '../../../contexts/PrivacyContext';
import { MONTHS } from '../../../utils/calendar/constants';
import { getDateKey, getISOWeekNumber } from '../../../utils/calendar/dateHelpers';

export type TodoVisibleFields = {
  priority: boolean;
  status: boolean;
  dueDate: boolean;
  assignee: boolean;
  created: boolean;
  notes: boolean;
  archivedAt: boolean;
};

export const DEFAULT_TODO_FIELDS: TodoVisibleFields = {
  priority: true,
  status: true,
  dueDate: true,
  assignee: true,
  created: true,
  notes: true,
  archivedAt: false,
};

// Per-view defaults
export const DEFAULT_BOARD_FIELDS: TodoVisibleFields = { ...DEFAULT_TODO_FIELDS };
export const DEFAULT_LIST_FIELDS: TodoVisibleFields = { ...DEFAULT_TODO_FIELDS };
export const DEFAULT_ARCHIVE_FIELDS: TodoVisibleFields = { ...DEFAULT_TODO_FIELDS, archivedAt: true };

// Shared toggle component for settings panels
const Toggle: React.FC<{ checked: boolean; onChange: () => void; label?: string }> = ({ checked, onChange, label }) => (
  <button
    onClick={onChange}
    className={`relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[var(--accent-primary)]' : 'bg-[var(--surface-tertiary)]'
    }`}
    role="switch"
    aria-checked={checked}
    aria-label={label}
  >
    <span className={`inline-block h-3 w-3 mx-0.5 rounded-full bg-white shadow-sm transition-transform ${
      checked ? 'translate-x-4' : 'translate-x-0'
    }`} />
  </button>
);

const SettingRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 px-3">
    <span className="text-xs text-[var(--content-secondary)]">{label}</span>
    {children}
  </div>
);

interface CalendarHeaderProps {
  // View state
  viewMode: 'year' | 'month' | 'week' | 'schedule' | 'todo';
  currentYear: number;
  currentMonth: number;
  currentWeekStart: Date;
  yearZoom?: boolean;
  yearLayout?: 'horizontal' | 'vertical';

  // Callbacks
  onViewModeChange: (mode: 'year' | 'month' | 'week' | 'schedule' | 'todo') => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onAddEvent: (dateKey: string, position: { x: number; y: number }) => void;
  onYearZoomToggle?: () => void;
  onYearLayoutToggle?: () => void;

  // Add todo from header
  onAddTodo?: (position: { x: number; y: number }) => void;

  // Global settings (shown in cal settings panel)
  showTodosOnCalendar?: boolean;
  onShowTodosOnCalendarChange?: (show: boolean) => void;

  // Task settings (todo view — opens BoardView panel)
  onTaskSettings?: () => void;
  taskSettingsHasChanges?: boolean;

  // Schedule settings (schedule view — opens ScheduleView panel)
  onScheduleSettings?: () => void;
  scheduleSettingsHasChanges?: boolean;

  // Print
  onPrint?: () => void;

  // Mobile
  onOpenDrawer?: () => void;
  onSetYear?: (year: number) => void;
  onSetMonth?: (month: number) => void;
  onSetWeek?: (date: Date) => void;
}

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  viewMode,
  currentYear,
  currentMonth,
  currentWeekStart,
  yearZoom = false,
  yearLayout = 'horizontal',
  onViewModeChange,
  onPrev,
  onNext,
  onToday,
  onAddEvent,
  onYearZoomToggle,
  onYearLayoutToggle,
  onAddTodo,
  onTaskSettings,
  taskSettingsHasChanges = false,
  scheduleSettingsHasChanges = false,
  onScheduleSettings,
  showTodosOnCalendar = true,
  onShowTodosOnCalendarChange,
  onPrint,
  onOpenDrawer,
  onSetYear,
  onSetMonth,
  onSetWeek,
}) => {
  const { theme, toggleTheme } = useTheme();
  const { privacyMode, togglePrivacyMode } = usePrivacy();
  const [showCalSettings, setShowCalSettings] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const calSettingsPanelRef = useRef<HTMLDivElement>(null);
  const calSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const periodDropdownRef = useRef<HTMLDivElement>(null);

  // Title animation — driven by the parent calling onPrev/onNext (which updates props)
  const [titleKey, setTitleKey] = useState(0);
  const [titleDir, setTitleDir] = useState<'prev' | 'next' | null>(null);
  const prevViewModeRef = useRef(viewMode);
  useEffect(() => {
    if (viewMode !== prevViewModeRef.current) {
      prevViewModeRef.current = viewMode;
      setTitleDir(null);
      setTitleKey(k => k + 1);
      setShowCalSettings(false);
    }
  }, [viewMode]);

  // Handle click outside for cal settings panel
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        calSettingsPanelRef.current && !calSettingsPanelRef.current.contains(e.target as Node) &&
        calSettingsButtonRef.current && !calSettingsButtonRef.current.contains(e.target as Node)
      ) {
        setShowCalSettings(false);
      }
    };
    if (showCalSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCalSettings]);

  // Handle click outside for period dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(e.target as Node)) {
        setShowPeriodDropdown(false);
      }
    };

    if (showPeriodDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPeriodDropdown]);

  const handlePrevious = () => {
    if (viewMode === 'todo') return;
    setTitleDir('prev');
    setTitleKey(k => k + 1);
    onPrev();
  };

  const handleNext = () => {
    if (viewMode === 'todo') return;
    setTitleDir('next');
    setTitleKey(k => k + 1);
    onNext();
  };

  const handleToday = () => {
    setTitleDir(null);
    setTitleKey(k => k + 1);
    onToday();
  };

  const handleAddClick = () => {
    const rect = addButtonRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (viewMode === 'todo') {
      onAddTodo?.({ x: rect.left, y: rect.bottom + 8 });
    } else {
      const d = new Date();
      const todayKey = getDateKey(d.getFullYear(), d.getMonth(), d.getDate());
      onAddEvent(todayKey, { x: rect.left, y: rect.bottom + 8 });
    }
  };

  const handleSettingsClick = () => {
    if (viewMode === 'todo') {
      onTaskSettings?.();
    } else if (viewMode === 'schedule') {
      onScheduleSettings?.();
    } else {
      setShowCalSettings(s => !s);
    }
  };

  // Dot indicator for calendar settings panel
  const calSettingsHasChanges =
    (viewMode === 'year' && (yearZoom || yearLayout === 'vertical')) ||
    privacyMode ||
    showTodosOnCalendar === false;

  const settingsHasChanges =
    viewMode === 'todo' ? taskSettingsHasChanges
    : viewMode === 'schedule' ? scheduleSettingsHasChanges
    : calSettingsHasChanges;

  // Mobile date label
  const mobileDateLabel = viewMode === 'todo'
    ? 'Tasks'
    : viewMode === 'year' || viewMode === 'schedule'
      ? `${currentYear}`
      : viewMode === 'month'
        ? `${MONTHS[currentMonth]} ${currentYear}`
        : `W${getISOWeekNumber(currentWeekStart)} · ${MONTHS[currentWeekStart.getMonth()]}`;

  // Desktop date label
  const desktopDateLabel = viewMode === 'todo'
    ? 'Tasks'
    : viewMode === 'year' || viewMode === 'schedule'
      ? currentYear
      : viewMode === 'month'
        ? <>{currentYear} <span className="text-[var(--content-tertiary)] font-normal">/</span> {MONTHS[currentMonth]}</>
        : <>{currentWeekStart.getFullYear()} <span className="text-[var(--content-tertiary)] font-normal">/</span> {MONTHS[currentWeekStart.getMonth()]} <span className="text-[var(--content-tertiary)] font-normal">/</span> W{getISOWeekNumber(currentWeekStart)}</>;

  return (
    <div className="relative flex items-center gap-2 sm:gap-3 mb-2 h-10">
      {/* ── MOBILE HEADER ── */}
      {/* Mozayc icon → opens drawer */}
      {onOpenDrawer && (
        <button
          onClick={onOpenDrawer}
          className="sm:hidden h-8 w-8 flex items-center justify-center rounded-lg active:bg-[var(--sidebar-hover)] transition-colors flex-shrink-0"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5 text-[var(--content-primary)]" />
        </button>
      )}

      {/* Mobile date label — tappable dropdown to select period */}
      <div className="sm:hidden relative flex-1 min-w-0" ref={periodDropdownRef}>
        <button
          onClick={() => viewMode !== 'todo' && setShowPeriodDropdown(!showPeriodDropdown)}
          className="flex items-center gap-1 h-10"
        >
          <span className="text-sm font-semibold text-[var(--content-primary)] tracking-tight whitespace-nowrap">
            {mobileDateLabel}
          </span>
          {viewMode !== 'todo' && (
            <ChevronDown className={`w-3.5 h-3.5 text-[var(--content-tertiary)] transition-transform ${showPeriodDropdown ? 'rotate-180' : ''}`} />
          )}
        </button>

        {/* Period dropdown */}
        {showPeriodDropdown && viewMode !== 'todo' && (
          <div className="absolute top-full left-0 mt-1 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-lg z-50 py-1 animate-slideDown overflow-y-auto max-h-[50vh]" style={{ boxShadow: 'var(--shadow-lg)' }}>
            {viewMode === 'year' || viewMode === 'schedule' ? (
              // Year picker — compact column
              <div className="py-0.5 w-[72px]">
                {Array.from({ length: 7 }, (_, i) => currentYear - 3 + i).map(year => (
                  <button
                    key={year}
                    onClick={() => { onSetYear?.(year); setShowPeriodDropdown(false); }}
                    className={`w-full px-2 py-1.5 text-xs text-center transition-colors rounded ${
                      year === currentYear ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-semibold' : 'text-[var(--content-primary)] active:bg-[var(--sidebar-hover)]'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            ) : viewMode === 'month' ? (
              // Month picker — 3-column grid
              <>
                <div className="flex items-center justify-between px-2 py-1.5 border-b border-[var(--border-secondary)]">
                  <button onClick={() => onSetYear?.(currentYear - 1)} className="p-1 rounded active:bg-[var(--sidebar-hover)]" aria-label="Previous year">
                    <ChevronLeft className="w-3.5 h-3.5 text-[var(--content-secondary)]" />
                  </button>
                  <span className="text-xs font-semibold text-[var(--content-primary)]">{currentYear}</span>
                  <button onClick={() => onSetYear?.(currentYear + 1)} className="p-1 rounded active:bg-[var(--sidebar-hover)]" aria-label="Next year">
                    <ChevronRight className="w-3.5 h-3.5 text-[var(--content-secondary)]" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-0.5 p-1.5">
                  {MONTHS.map((month, idx) => (
                    <button
                      key={idx}
                      onClick={() => { onSetMonth?.(idx); setShowPeriodDropdown(false); }}
                      className={`px-2 py-1.5 text-xs rounded-md text-center transition-colors ${
                        idx === currentMonth ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-semibold' : 'text-[var(--content-primary)] active:bg-[var(--sidebar-hover)]'
                      }`}
                    >
                      {month.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </>
            ) : viewMode === 'week' ? (
              // Week picker — compact rows
              <>
                <div className="flex items-center justify-between px-2 py-1.5 border-b border-[var(--border-secondary)]">
                  <button onClick={() => {
                    const prev = new Date(currentWeekStart);
                    prev.setMonth(prev.getMonth() - 1);
                    onSetWeek?.(prev);
                  }} className="p-1 rounded active:bg-[var(--sidebar-hover)]" aria-label="Previous month">
                    <ChevronLeft className="w-3.5 h-3.5 text-[var(--content-secondary)]" />
                  </button>
                  <span className="text-xs font-semibold text-[var(--content-primary)]">{MONTHS[currentWeekStart.getMonth()].slice(0, 3)} {currentWeekStart.getFullYear()}</span>
                  <button onClick={() => {
                    const next = new Date(currentWeekStart);
                    next.setMonth(next.getMonth() + 1);
                    onSetWeek?.(next);
                  }} className="p-1 rounded active:bg-[var(--sidebar-hover)]" aria-label="Next month">
                    <ChevronRight className="w-3.5 h-3.5 text-[var(--content-secondary)]" />
                  </button>
                </div>
                <div className="py-0.5">
                  {(() => {
                    const month = currentWeekStart.getMonth();
                    const year = currentWeekStart.getFullYear();
                    const weeks: Date[] = [];
                    const d = new Date(year, month, 1);
                    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
                    while (d.getMonth() <= month || weeks.length < 4) {
                      weeks.push(new Date(d));
                      d.setDate(d.getDate() + 7);
                      if (weeks.length >= 6) break;
                    }
                    return weeks.map((weekStart, i) => {
                      const weekEnd = new Date(weekStart);
                      weekEnd.setDate(weekEnd.getDate() + 6);
                      const wn = getISOWeekNumber(weekStart);
                      const isCurrent = currentWeekStart.getTime() === weekStart.getTime();
                      return (
                        <button
                          key={i}
                          onClick={() => { onSetWeek?.(weekStart); setShowPeriodDropdown(false); }}
                          className={`w-full px-3 py-1.5 text-xs text-left transition-colors flex items-center gap-2 ${
                            isCurrent ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-semibold' : 'text-[var(--content-primary)] active:bg-[var(--sidebar-hover)]'
                          }`}
                        >
                          <span className="text-[var(--content-tertiary)] w-6 text-right font-mono">W{wn}</span>
                          <span>{weekStart.getDate()}–{weekEnd.getDate()} {weekEnd.toLocaleDateString('en-US', { month: 'short' })}</span>
                        </button>
                      );
                    });
                  })()}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Mobile today button */}
      {viewMode !== 'todo' && (
        <button
          onClick={handleToday}
          className="sm:hidden h-8 w-8 flex items-center justify-center rounded-lg active:bg-[var(--sidebar-hover)] transition-colors flex-shrink-0"
          aria-label="Go to today"
        >
          <CalendarDays className="w-4 h-4 text-[var(--content-secondary)]" />
        </button>
      )}

      {/* Mobile privacy toggle */}
      {viewMode !== 'todo' && viewMode !== 'schedule' && (
        <button
          onClick={togglePrivacyMode}
          className="sm:hidden h-8 w-8 flex items-center justify-center rounded-lg active:bg-[var(--sidebar-hover)] transition-colors flex-shrink-0"
          aria-label={privacyMode ? 'Show event names' : 'Hide event names'}
        >
          {privacyMode ? (
            <EyeOff className="w-4 h-4 text-[var(--content-secondary)]" />
          ) : (
            <Eye className="w-4 h-4 text-[var(--content-secondary)]" />
          )}
        </button>
      )}

      {/* ── DESKTOP HEADER ── */}
      {/* Add Button */}
      <div className="hidden sm:block flex-shrink-0">
        <button
          ref={addButtonRef}
          onClick={handleAddClick}
          className="h-8 w-8 bg-[var(--surface-tertiary)] text-[var(--content-primary)] text-xs font-medium rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors flex items-center justify-center"
          title="Add"
          aria-label={viewMode === 'todo' ? 'Add task' : 'Add event'}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      {viewMode !== 'todo' && (
        <div className="hidden sm:flex items-center gap-0.5">
          <button
            onClick={handlePrevious}
            className="h-8 w-8 flex items-center justify-center hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4 text-[var(--content-secondary)]" />
          </button>

          <button
            onClick={handleToday}
            className="h-8 px-3 text-[var(--content-primary)] text-xs font-medium hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors"
          >
            Today
          </button>

          <button
            onClick={handleNext}
            className="h-8 w-8 flex items-center justify-center hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4 text-[var(--content-secondary)]" />
          </button>
        </div>
      )}

      {/* Desktop Date Label */}
      <div className="hidden sm:flex items-center h-10 flex-shrink-0 w-[175px] overflow-hidden">
        <span
          key={titleKey}
          className={`text-sm font-semibold text-[var(--content-primary)] tracking-tight whitespace-nowrap ${
            titleDir === 'next' ? 'animate-slideFromRight'
            : titleDir === 'prev' ? 'animate-slideFromLeft'
            : 'animate-fadeInView'
          }`}
        >
          {desktopDateLabel}
        </span>
      </div>

      {/* View Toggle - Desktop, absolutely centered */}
      <div className="hidden sm:flex absolute left-1/2 -translate-x-1/2 items-center bg-[var(--surface-tertiary)] rounded-lg p-0.5">
        {(['todo', 'schedule', 'week', 'month', 'year'] as const).map((mode) => (
          <button
            key={mode}
            onClick={() => onViewModeChange(mode)}
            className={`px-3 py-1 text-xs font-normal rounded transition-colors ${
              viewMode === mode
                ? 'bg-[var(--calendar-bg-solid)] text-[var(--content-primary)]'
                : 'text-[var(--content-secondary)] hover:text-[var(--content-primary)]'
            }`}
            style={viewMode === mode ? { boxShadow: 'var(--shadow-sm)' } : undefined}
          >
            {mode === 'todo' ? 'Tasks' : mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>

      {/* Right group (desktop only) */}
      <div className="ml-auto hidden sm:flex items-center gap-1.5">

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="flex h-8 w-8 items-center justify-center hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? (
            <Sun className="w-4 h-4 text-[var(--content-secondary)]" />
          ) : (
            <Moon className="w-4 h-4 text-[var(--content-secondary)]" />
          )}
        </button>

        {/* Unified Settings Button — rightmost, all views */}
        <button
          ref={calSettingsButtonRef}
          onClick={handleSettingsClick}
          data-ignore-outside-click="settings-panel"
          className={`relative h-8 w-8 flex items-center justify-center rounded-lg transition-colors ${
            settingsHasChanges
              ? 'text-[var(--accent-primary)] hover:bg-[var(--accent-subtle)]'
              : (showCalSettings ? 'bg-[var(--surface-tertiary)] text-[var(--content-primary)]' : 'hover:bg-[var(--sidebar-hover)] text-[var(--content-secondary)]')
          }`}
          title="Settings"
          aria-label="Settings"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {settingsHasChanges && (
            <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)]" />
          )}
        </button>

        {/* Calendar settings panel (month / week / year) */}
        {showCalSettings && ['month', 'week', 'year'].includes(viewMode) && createPortal(
          <div
            ref={calSettingsPanelRef}
            style={{ position: 'fixed', top: 52, right: 12, zIndex: 200, minWidth: 224 }}
            className="bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-xl shadow-lg py-2 animate-slideDown"
          >
            {/* Year Layout — year view only, shown first */}
            {viewMode === 'year' && onYearLayoutToggle && (
              <SettingRow label="Layout">
                <div className="flex items-center bg-[var(--surface-tertiary)] rounded-md p-0.5 gap-0.5">
                  <button
                    onClick={() => yearLayout !== 'horizontal' && onYearLayoutToggle?.()}
                    title="Horizontal"
                    className={`px-2.5 py-1 rounded transition-colors ${
                      yearLayout === 'horizontal'
                        ? 'bg-[var(--calendar-bg-solid)] text-[var(--content-primary)]'
                        : 'text-[var(--content-secondary)] hover:text-[var(--content-primary)]'
                    }`}
                    style={yearLayout === 'horizontal' ? { boxShadow: 'var(--shadow-sm)' } : undefined}
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => yearLayout !== 'vertical' && onYearLayoutToggle?.()}
                    title="Vertical"
                    className={`px-2.5 py-1 rounded transition-colors ${
                      yearLayout === 'vertical'
                        ? 'bg-[var(--calendar-bg-solid)] text-[var(--content-primary)]'
                        : 'text-[var(--content-secondary)] hover:text-[var(--content-primary)]'
                    }`}
                    style={yearLayout === 'vertical' ? { boxShadow: 'var(--shadow-sm)' } : undefined}
                  >
                    <LayoutList className="w-3.5 h-3.5" />
                  </button>
                </div>
              </SettingRow>
            )}

            {/* Show tasks */}
            <SettingRow label="Show tasks">
              <Toggle
                checked={showTodosOnCalendar}
                onChange={() => onShowTodosOnCalendarChange?.(!showTodosOnCalendar)}
                label="Show tasks"
              />
            </SettingRow>

            {/* Privacy */}
            <SettingRow label="Blur events">
              <Toggle
                checked={privacyMode}
                onChange={togglePrivacyMode}
                label="Blur events"
              />
            </SettingRow>

            {/* Year Zoom — year view only */}
            {viewMode === 'year' && onYearZoomToggle && (
              <SettingRow label="Zoom (6 months)">
                <Toggle
                  checked={yearZoom}
                  onChange={onYearZoomToggle}
                  label="Zoom"
                />
              </SettingRow>
            )}

            <hr className="my-1 border-[var(--border-secondary)]" />

            {/* Export */}
            <button
              onClick={() => { onPrint?.(); setShowCalSettings(false); }}
              className="w-full px-3 py-1.5 text-left text-xs text-[var(--content-primary)] hover:bg-[var(--surface-tertiary)] flex items-center gap-2 transition-colors"
            >
              <Printer className="w-3.5 h-3.5" />
              Export
            </button>
          </div>,
          document.body
        )}

      </div>{/* end right group */}
    </div>
  );
};
